import {
  BookHeart,
  BrushCleaning,
  CalendarDays,
  Camera,
  Gift,
  Heart,
  HeartHandshake,
  Home,
  Image as ImageIcon,
  ListChecks,
  MessageCircle,
  PersonStanding,
  PiggyBank,
  Plane,
  Settings,
  Sparkles,
  Star,
  Users,
  Utensils,
  type LucideIcon,
} from "lucide-react";

import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

import { NAV_ITEMS, type NavKey } from "../components/nav";
import { queryKeys } from "./queryKeys";
import { useToast } from "./toast";
import { supabase } from "./supabase";

/**
 * メニューのユーザーごとの設定。すべて任意で、無い項目は既定のまま。
 * 保存形が壊れていても落ちないよう、読むときに resolveNavItems が検証する。
 */
export interface NavPrefs {
  /** タブの並び(NavKey の列)。載っていないタブは既定順で後ろに付く */
  order?: string[];
  /** 隠すタブ。設定タブは隠せない(編集への入口を失わないため) */
  hidden?: string[];
  /** タブごとの表示名の上書き */
  labels?: Record<string, string>;
  /** タブごとのアイコン名(NAV_ICON_CHOICES のキー)の上書き */
  icons?: Record<string, string>;
}

/** アイコンの選択肢。lucide を名前で参照できるよう、使えるものをここで絞る */
export const NAV_ICON_CHOICES: Record<string, LucideIcon> = {
  image: ImageIcon,
  camera: Camera,
  heart: Heart,
  "heart-handshake": HeartHandshake,
  "book-heart": BookHeart,
  calendar: CalendarDays,
  "list-checks": ListChecks,
  "piggy-bank": PiggyBank,
  cleaning: BrushCleaning,
  person: PersonStanding,
  users: Users,
  star: Star,
  home: Home,
  sparkles: Sparkles,
  message: MessageCircle,
  gift: Gift,
  plane: Plane,
  utensils: Utensils,
  settings: Settings,
};

/** 既定+設定を合成した、画面に出す1タブぶん */
export interface ResolvedNavItem {
  key: NavKey;
  label: string;
  icon: LucideIcon;
  /** 上書きしたアイコン名。編集画面で「選択中」を出すために持つ */
  iconName: string | null;
  hidden: boolean;
}

function stringArrayOf(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];
}

function stringMapOf(value: unknown): Record<string, string> {
  if (typeof value !== "object" || value === null) return {};
  const entries = Object.entries(value as Record<string, unknown>).filter(
    (entry): entry is [string, string] => typeof entry[1] === "string",
  );
  return Object.fromEntries(entries);
}

/** 既定のタブ一覧に設定を重ねる。設定が無い・壊れているときは既定のまま */
export function resolveNavItems(prefs: NavPrefs | null): ResolvedNavItem[] {
  const order = stringArrayOf(prefs?.order);
  const hidden = new Set(stringArrayOf(prefs?.hidden));
  const labels = stringMapOf(prefs?.labels);
  const icons = stringMapOf(prefs?.icons);

  const sorted = [...NAV_ITEMS].sort((a, b) => {
    const defaultA = NAV_ITEMS.findIndex((item) => item.key === a.key);
    const defaultB = NAV_ITEMS.findIndex((item) => item.key === b.key);
    const posA = order.indexOf(a.key);
    const posB = order.indexOf(b.key);
    // 並びに無いタブ(後から増えた機能)は既定の位置関係のまま末尾へ
    const rankA = posA === -1 ? order.length + defaultA : posA;
    const rankB = posB === -1 ? order.length + defaultB : posB;
    return rankA - rankB;
  });

  return sorted.map((item) => {
    const label = labels[item.key]?.trim();
    const iconName = icons[item.key] ?? null;
    return {
      key: item.key,
      label: label !== undefined && label !== "" ? label : item.label,
      icon: (iconName !== null ? NAV_ICON_CHOICES[iconName] : undefined) ?? item.icon,
      iconName: iconName !== null && iconName in NAV_ICON_CHOICES ? iconName : null,
      // 設定タブを隠すと、メニュー編集への入口ごと失いかねない
      hidden: item.key === "settings" ? false : hidden.has(item.key),
    };
  });
}

/** 設定を重ねたタブ一覧。下部ナビと各画面の見出しの両方がこれを使うことで、
    表示名・アイコンの変更がどこでも同じに見える */
export function useNavItems(): ResolvedNavItem[] {
  const toast = useToast();
  const prefs = useQuery({ queryKey: queryKeys.navPrefs, queryFn: fetchNavPrefs });
  // 失敗しても既定のメニューで動き続けるが、「設定が消えた」と見えないよう一言だけ知らせる。
  // ナビと見出しの複数箇所から呼ばれても、同文のトーストは1枚にまとめられる
  const failed = prefs.isError;
  useEffect(() => {
    if (failed) toast("メニュー設定の取得に失敗しました。", "error");
  }, [failed, toast]);
  return resolveNavItems(prefs.data ?? null);
}

/** 自分のメニュー設定。未設定なら null。
    列を名指ししないのはマイグレーション未適用でも壊れないようにするため */
export async function fetchNavPrefs(): Promise<NavPrefs | null> {
  const { data, error } = await supabase.auth.getUser();
  if (error) throw error;
  const response = await supabase.from("profiles").select("*").eq("id", data.user.id);
  if (response.error) throw response.error;
  const rows = response.data as { nav_prefs?: unknown }[];
  const prefs: unknown = rows[0]?.nav_prefs;
  return typeof prefs === "object" && prefs !== null ? prefs : null;
}

export async function saveNavPrefs(userId: string, prefs: NavPrefs): Promise<void> {
  const { error } = await supabase.from("profiles").update({ nav_prefs: prefs }).eq("id", userId);
  if (error) throw error;
}

/** 初期状態(既定の並び・全部表示)に戻す */
export async function resetNavPrefs(userId: string): Promise<void> {
  const { error } = await supabase.from("profiles").update({ nav_prefs: null }).eq("id", userId);
  if (error) throw error;
}
